import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Star, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { ProductGallery } from "@/components/ProductGallery";
import { NewBadge } from "@/components/NewBadge";
import { catsOf } from "@/lib/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `₹${n.toLocaleString("en-IN")}`;

const SIZE_GUIDE = [
  ["XS", "32", "26", "35"],
  ["S", "34", "28", "37"],
  ["M", "36", "30", "39"],
  ["L", "38", "32", "41"],
  ["XL", "40", "34", "43"],
  ["XXL", "42", "36", "45"],
];

export default function Product() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useStore();
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("M");
  const [color, setColor] = useState(null);
  const [pincode, setPincode] = useState("");
  const [eta, setEta] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => {
      setProducts(r.data);
      const found = r.data.find(p => p.id === id);
      setProduct(found);
      if (found) setSize(found.sizes?.includes("M") ? "M" : found.sizes?.[0] || "M");
      setColor(found?.colors?.[0] || null);
      setEta(null);
      setPincode("");
      window.scrollTo(0, 0);
    });
  }, [id]);

  const checkEta = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(pincode)) { setEta({ error: "Enter a valid 6-digit pincode." }); return; }
    try {
      const r = await axios.get(`${API}/delivery-eta`, { params: { pincode } });
      setEta(r.data);
    } catch { setEta({ error: "Couldn't check right now. Try again." }); }
  };

  const addAndGo = () => { add(product, size, 1, color); nav("/cart"); };

  if (!product) return <div className="product-loading" data-testid="product-loading">Loading…</div>;
  const related = products.filter(p => p.id !== product.id && catsOf(p).some(c => catsOf(product).includes(c))).slice(0, 3);

  return (
    <div className="product-page" data-testid="product-page">
      <Link to="/shop" className="crumb"><ChevronLeft size={14} /> Back to shop</Link>
      <div className="product-layout">
        <ProductGallery product={product} lead={color?.image || ""} />
        <div className="product-details">
          <p className="lux-eyebrow dark" data-testid="product-categories">{catsOf(product).join(" · ")}</p>
          <h1 data-testid="product-title">{product.name}</h1>
          <div className="product-price-row">
            <strong data-testid="product-price">{money(product.price)}</strong>
            <del>{money(product.original_price)}</del>
            <span className="save-tag">Save {money(product.original_price - product.price)}</span>
          </div>
          <div className="product-rating"><Star size={13} fill="currentColor" /> {product.rating} · Reviewed by our atelier team</div>
          <p className="product-desc">{product.description}</p>

          {product.colors?.length > 0 && (
            <div className="color-row-wrap" data-testid="product-colors">
              <p className="lux-label">Colour · <span data-testid="product-selected-color">{color?.name}</span></p>
              <div className="product-colors">
                {product.colors.map(c => (
                  <button type="button" key={c.name} className={`${color?.name === c.name ? "selected" : ""} ${c.image ? "" : "no-photo"}`} onClick={() => setColor(c)} data-testid={`product-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    {c.image && <img src={c.image} alt="" />}{c.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="size-row">
            <div className="size-row-head">
              <p className="lux-label">Select size</p>
              <button type="button" className="link-btn" onClick={() => setGuideOpen(!guideOpen)} data-testid="size-guide-toggle">Size guide {guideOpen ? "×" : "↗"}</button>
            </div>
            <div className="product-sizes">
              {product.sizes.map(s => <button key={s} className={size === s ? "selected" : ""} onClick={() => setSize(s)} data-testid={`product-size-${s}`}>{s}</button>)}
            </div>
            {guideOpen && (
              <div className="size-guide" data-testid="size-guide">
                <table>
                  <thead><tr><th>Size</th><th>Bust (in)</th><th>Waist (in)</th><th>Hip (in)</th></tr></thead>
                  <tbody>{SIZE_GUIDE.map(row => <tr key={row[0]}><td>{row[0]}</td><td>{row[1]}</td><td>{row[2]}</td><td>{row[3]}</td></tr>)}</tbody>
                </table>
                <small>Body measurements. Garments have appropriate ease worked in.</small>
              </div>
            )}
          </div>

          <button className="lux-cta full" onClick={addAndGo} data-testid="add-to-cart-button">Add to bag · {money(product.price)}</button>

          <form className="eta-check" onSubmit={checkEta} data-testid="eta-form">
            <p className="lux-label">Delivery ETA</p>
            <div className="eta-input-row">
              <input value={pincode} onChange={e => setPincode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit pincode" maxLength={6} data-testid="eta-pincode-input" />
              <button type="submit" data-testid="eta-submit">Check</button>
            </div>
            {eta && (eta.error ? <p className="eta-error" data-testid="eta-error">{eta.error}</p> : <p className="eta-ok" data-testid="eta-result"><Truck size={14} /> {eta.message}</p>)}
          </form>

          <ul className="trust-row">
            <li><Truck size={14} /> Free shipping over ₹2,500</li>
            <li><RotateCcw size={14} /> 7-day easy exchange</li>
            <li><ShieldCheck size={14} /> Secure Razorpay checkout</li>
          </ul>
        </div>
      </div>

      {related.length > 0 && (
        <section className="related" data-testid="related-section">
          <h2>You may also like</h2>
          <div className="related-grid">
            {related.map(p => (
              <Link key={p.id} to={`/product/${p.id}`} className="lux-card" data-testid={`related-${p.id}`}>
                <div className="lux-card-image"><img src={p.image} alt={p.name} /><NewBadge product={p} /></div>
                <div className="lux-card-info">
                  <p className="lux-card-cat">{p.category}</p>
                  <h3>{p.name}</h3>
                  <p className="lux-card-price">{money(p.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
